import { useCallback, useEffect, useState } from 'react'
import type { LigneJournal, Vendeur } from '@troc/shared'
import { eur } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { PorteGerant } from '../composants/PorteGerant'
import { useToast } from '../etat/Toast'

/**
 * Le rapport d'activité, réservé aux gérants.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * À quelle question il répond
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Toujours une question particulière : « qui a soldé cette pièce à −40 % ? »,
 * « qu'a fait Julian jeudi après-midi ? », « pourquoi ce contrat a changé ? ».
 * D'où un journal détaillé plutôt qu'un tableau de moyennes — et le détail
 * conserve l'**avant**, sans quoi on sait qu'un prix a bougé mais pas depuis
 * quoi, ce qui ne permet jamais de rattraper l'erreur.
 *
 * Le résumé par personne est là en contrepoint, pour la lecture de fin de
 * mois. Il ne sert pas à noter les gens : quatre vendeurs ne se comparent pas
 * sur un nombre d'actions, ils tiennent des postes différents.
 */

interface ResumeVendeur {
  id: number | null
  nom: string
  actions: number
  ventes: number
  encaisseCentimes: number
  depots: number
  corrections: number
}

interface Rapports {
  journal: LigneJournal[]
  parVendeur: ResumeVendeur[]
  actions: { cle: string; libelle: string }[]
}

const FORMAT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

function jourIso(decalage = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + decalage)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

export function Rapport() {
  return <PorteGerant>{(entetes) => <Journal entetes={entetes} />}</PorteGerant>
}

function Journal({ entetes }: { entetes: Record<string, string> }) {
  const { alerter, signaler } = useToast()
  const [donnees, setDonnees] = useState<Rapports | null>(null)
  const [equipe, setEquipe] = useState<Vendeur[]>([])
  const [vendeur, setVendeur] = useState('')
  const [action, setAction] = useState('')
  const [du, setDu] = useState(jourIso(-30))
  const [au, setAu] = useState(jourIso())
  const [requete, setRequete] = useState('')
  const [ouverte, setOuverte] = useState<number | null>(null)
  const [portraits, setPortraits] = useState(false)

  const charger = useCallback(() => {
    const p = new URLSearchParams({ du, au, limite: '300' })
    if (vendeur) p.set('vendeur', vendeur)
    if (action) p.set('action', action)
    if (requete) p.set('q', requete)
    api<Rapports>(`/journal?${p}`, { entetes })
      .then(setDonnees)
      .catch(() => undefined)
  }, [entetes, du, au, vendeur, action, requete])

  useEffect(() => {
    const minuterie = window.setTimeout(charger, 220)
    return () => window.clearTimeout(minuterie)
  }, [charger])

  useEffect(() => {
    api<{ equipe: Vendeur[] }>('/equipe')
      .then((r) => setEquipe(r.equipe))
      .catch(() => undefined)
  }, [])

  if (!donnees) {
    return (
      <Ecran titre="Rapport" sousTitre="Chargement…">
        <div className="vide">Lecture du journal…</div>
      </Ecran>
    )
  }

  return (
    <Ecran
      titre="Rapport"
      sousTitre="Journal des actions · réservé aux gérants"
      outils={
        <button
          type="button"
          className="btn btn-secondaire"
          style={{ marginLeft: 'auto' }}
          onClick={() => setPortraits((p) => !p)}
        >
          {portraits ? 'Fermer les portraits' : 'Portraits de l’équipe'}
        </button>
      }
    >
      {portraits && (
        <Portraits
          equipe={equipe}
          entetes={entetes}
          onMaj={(v) => setEquipe((liste) => liste.map((x) => (x.id === v.id ? v : x)))}
          onErreur={alerter}
          onInfo={signaler}
        />
      )}

      <div className="barre-filtres">
        <select className="saisie" value={vendeur} onChange={(e) => setVendeur(e.target.value)}>
          <option value="">Toute l’équipe</option>
          {equipe.map((v) => (
            <option key={v.id} value={v.id}>
              {v.nom}
            </option>
          ))}
        </select>
        <select className="saisie" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">Toutes les actions</option>
          {donnees.actions.map((a) => (
            <option key={a.cle} value={a.cle}>
              {a.libelle}
            </option>
          ))}
        </select>
        <input
          className="saisie"
          type="date"
          aria-label="Du"
          value={du}
          onChange={(e) => setDu(e.target.value)}
        />
        <input
          className="saisie"
          type="date"
          aria-label="Au"
          value={au}
          onChange={(e) => setAu(e.target.value)}
        />
        <input
          className="saisie"
          style={{ flex: 1, minWidth: 180 }}
          placeholder="Rechercher — n° d’article, client, ticket…"
          value={requete}
          onChange={(e) => setRequete(e.target.value)}
        />
      </div>

      <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <section className="bloc-fiche">
          <h3 className="bloc-fiche-titre">Sur la période retenue</h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Personne</th>
                <th className="a-droite">Actions</th>
                <th className="a-droite">Ventes</th>
                <th className="a-droite">Encaissé</th>
                <th className="a-droite">Dépôts</th>
                <th className="a-droite">Corrections</th>
              </tr>
            </thead>
            <tbody>
              {donnees.parVendeur.map((v) => (
                <tr key={v.id ?? 'nul'}>
                  <td className="titre">{v.nom}</td>
                  <td className="a-droite chiffre">{v.actions}</td>
                  <td className="a-droite">{v.ventes}</td>
                  <td className="a-droite chiffre">{eur(v.encaisseCentimes)}</td>
                  <td className="a-droite">{v.depots}</td>
                  <td className="a-droite">{v.corrections}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {donnees.parVendeur.length === 0 && (
            <div className="vide">Aucune action sur cette période.</div>
          )}
        </section>

        <section className="bloc-fiche">
          <h3 className="bloc-fiche-titre">
            Journal · {donnees.journal.length} action{donnees.journal.length > 1 ? 's' : ''}
          </h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Quand</th>
                <th>Qui</th>
                <th>Quoi</th>
                <th>Détail</th>
                <th className="a-droite">Montant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {donnees.journal.map((l) => {
                const detaille = l.avant !== null || l.apres !== null
                return (
                  <>
                    <tr key={l.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {FORMAT.format(new Date(l.horodatage))}
                      </td>
                      <td className="titre">
                        {l.vendeurNom ?? '—'}
                        {/* Le compte de la session n'est signalé que s'il
                            diffère : sinon c'est du bruit à chaque ligne. */}
                        {l.sessionNom && l.sessionNom !== l.vendeurNom && (
                          <span className="sourdine" style={{ fontSize: 11 }}>
                            {' '}
                            (session {l.sessionNom})
                          </span>
                        )}
                      </td>
                      <td className="sourdine">{l.libelleAction}</td>
                      <td>{l.resume}</td>
                      <td className="a-droite chiffre">
                        {l.montantCentimes !== null ? eur(l.montantCentimes) : ''}
                      </td>
                      <td className="a-droite">
                        {detaille && (
                          <button
                            type="button"
                            className="btn btn-fantome"
                            onClick={() => setOuverte(ouverte === l.id ? null : l.id)}
                          >
                            {ouverte === l.id ? 'Masquer' : 'Détail'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {ouverte === l.id && (
                      <tr key={`${l.id}-detail`}>
                        <td colSpan={6} className="journal-detail">
                          <div className="avant-apres">
                            {l.avant !== null && (
                              <div>
                                <div className="etiquette">Avant</div>
                                <pre>{JSON.stringify(l.avant, null, 2)}</pre>
                              </div>
                            )}
                            {l.apres !== null && (
                              <div>
                                <div className="etiquette">Après</div>
                                <pre>{JSON.stringify(l.apres, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
          {donnees.journal.length === 0 && (
            <div className="vide">Rien à afficher pour ces filtres.</div>
          )}
        </section>
      </div>
    </Ecran>
  )
}

/**
 * Chargement des portraits.
 *
 * L'image est réduite à 256 × 256 dans le navigateur avant d'être envoyée :
 * une photo de téléphone pèse quatre mégaoctets, ce qui n'a rien à faire dans
 * une base qu'on sauvegarde tous les soirs.
 */
function Portraits({
  equipe,
  entetes,
  onMaj,
  onErreur,
  onInfo,
}: {
  equipe: Vendeur[]
  entetes: Record<string, string>
  onMaj: (vendeur: Vendeur) => void
  onErreur: (message: string) => void
  onInfo: (message: string) => void
}) {
  const envoyer = async (vendeur: Vendeur, fichier: File) => {
    try {
      const photo = await reduire(fichier)
      const reponse = await api<{ vendeur: Vendeur }>(`/equipe/${vendeur.id}/photo`, {
        methode: 'PUT',
        corps: { photo },
        entetes,
      })
      onMaj(reponse.vendeur)
      onInfo(`Portrait de ${vendeur.nom} enregistré.`)
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Image illisible.')
    }
  }

  return (
    <div className="portraits-equipe">
      <div className="etiquette">Portraits de l’équipe</div>
      <div className="visages">
        {equipe.map((vendeur) => (
          <label key={vendeur.id} className="visage" style={{ cursor: 'pointer' }}>
            <span className="portrait">
              {vendeur.photo ? (
                <img src={vendeur.photo} alt="" />
              ) : (
                <span className="initiales">{vendeur.initiales}</span>
              )}
            </span>
            <span className="prenom">{vendeur.nom}</span>
            <span className="sourdine" style={{ fontSize: 10 }}>
              {vendeur.photo ? 'remplacer' : 'ajouter'}
            </span>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const fichier = e.target.files?.[0]
                if (fichier) void envoyer(vendeur, fichier)
                e.target.value = ''
              }}
            />
          </label>
        ))}
      </div>
      <div className="note" style={{ marginTop: 10 }}>
        Une image carrée fonctionne mieux : elle est recadrée au centre et réduite à 256 × 256
        avant d’être enregistrée dans la base — donc emportée par la sauvegarde du soir, sans
        dossier d’images à gérer à côté.
      </div>
    </div>
  )
}

/** Recadre au carré, réduit à 256 px, et rend un data URI JPEG. */
function reduire(fichier: File): Promise<string> {
  return new Promise((resoudre, rejeter) => {
    const lecteur = new FileReader()
    lecteur.onerror = () => rejeter(new Error('Lecture impossible.'))
    lecteur.onload = () => {
      const image = new Image()
      image.onerror = () => rejeter(new Error('Image illisible.'))
      image.onload = () => {
        const cote = Math.min(image.width, image.height)
        const toile = document.createElement('canvas')
        toile.width = 256
        toile.height = 256
        const ctx = toile.getContext('2d')
        if (!ctx) return rejeter(new Error('Redimensionnement impossible.'))
        ctx.drawImage(
          image,
          (image.width - cote) / 2,
          (image.height - cote) / 2,
          cote,
          cote,
          0,
          0,
          256,
          256,
        )
        resoudre(toile.toDataURL('image/jpeg', 0.82))
      }
      image.src = lecteur.result as string
    }
    lecteur.readAsDataURL(fichier)
  })
}
